import { Component, DestroyRef, HostBinding, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	ActivatedRoute,
	NavigationEnd,
	Router,
	RouterOutlet,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { Company } from 'src/app/models/data/company';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserProfile } from 'src/app/models/model';
import { EntityRelationship } from 'src/app/models/data/entityRelationship';
import { Timezone } from 'src/app/models/timezone';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { AUDIT_LOG_COLUMN_COMPANY } from 'src/app/app.constants';
import { AppTabNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import {
	MatTabGroup,
	MatTab,
	MatTabContent,
	MatTabLabel,
} from '@angular/material/tabs';
import { QueryStringTabGroupDirective } from '../../../directives/query-string-tab-group.directive';
import { CompanyAboutComponent } from './company-about/company-about.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { CompanyDevicesComponent } from './company-devices/company-devices.component';
import { CompanyDeviceTabsComponent } from './company-device-tabs/company-device-tabs.component';
import { CompanyPropertiesComponent } from './company-properties/company-properties.component';
import { CompanyRelationshipsComponent } from './company-relationships/company-relationships.component';
import { CompanyUserComponent } from './company-user/company-user.component';
import { CompanyRolesComponent } from './company-roles/company-roles.component';
import { AuditLogListComponent } from '../../audit-log-list/audit-log-list.component';
import { CompanyUsersComponent } from './company-users/company-users.component';
import { SubNavComponent } from '../../../components/sub-nav/sub-nav.component';
import { PropertyListTableComponent } from '../../property/property-list-table/property-list-table.component';

export type CompanyDetailResponse = {
	company: Company;
	relationship?: EntityRelationship;
	timezone?: Timezone;
};

@Component({
	selector: 'app-company-detail-main-tab',
	templateUrl: './company-detail-main-tab.component.html',
	styleUrls: ['./company-detail-main-tab.component.scss'],
	imports: [
		AppTabNavigationHeaderComponent,
		MatTabGroup,
		QueryStringTabGroupDirective,
		MatTab,
		MatTabContent,
		MatTabLabel,
		CompanyAboutComponent,
		BreadcrumbLabelDirective,
		CompanyDevicesComponent,
		CompanyDeviceTabsComponent,
		CompanyPropertiesComponent,
		CompanyRelationshipsComponent,
		CompanyUserComponent,
		CompanyRolesComponent,
		AuditLogListComponent,
		CompanyUsersComponent,
		SubNavComponent,
		PropertyListTableComponent,
		RouterOutlet,
	],
})
export class CompanyDetailMainTabComponent implements OnInit {
	companyId!: string;

	companyAboutBreadcrumbLabel = '';
	companyDevicesBreadcrumbLabel = '';
	companyPropertiesBreadcrumbLabel = '';
	companyRelationshipsBreadcrumbLabel = '';
	companyUserBreadcrumbLabel = '';
	companyRolesBreadcrumbLabel = '';
	auditLogBreadcrumbLabel = '';

	company?: Company;
	/**
	 * In-flight company GET for the first About paint only.
	 * Cleared after shell init so later About instances (matTabContent recreate) refetch.
	 */
	initialCompanyResponse?: Promise<CompanyDetailResponse>;
	hasGlobalCompanyManagement = false;
	/** GCM act or GCM View — show cross-company company tabs (Support read path). */
	hasGcmView = false;
	/** Billing (Internal) — Users view-only without GCM act (ITE-1887). */
	hasBillingManagement = false;
	canAccessAuditLogs = false;

	hiddenAuditLogColumns = [AUDIT_LOG_COLUMN_COMPANY];

	canViewProperties = false;
	/** Oracle Add on the embedded Properties list (ITE-11994); Support/Billing stay false. */
	canAddProperty = false;
	companyIsSentrilock = false;
	isEditingActiveCompany = false;

	/** F3145 kill switch — new tab order + Users All Users/Whitelist embed. */
	enableUpdatedSysAdmin = false;
	canSeeWhitelistTab = false;
	/** Oracle / GCM act / CA-on-active — false for Support View and Billing (ITE-1887). */
	allowWhitelistMutate = false;
	/** Oracle / GCM act / DM-on-active — false for Support View, Billing, SentriLock (ITE-12004). */
	allowDeviceWrites = false;
	companyDetailsReady = false;

	/**
	 * Child route `property/:propertyId/detail` is active — keep company chrome,
	 * Properties selected, list hidden (ITE-12009 Single Property shell).
	 */
	@HostBinding('class.property-shell-active')
	propertyShellActive = false;

	/** Same accessible set a User of the route company sees on /properties. */
	readonly companyPropertyDataRequest = async (filter: QueryParam[]) => {
		if (!this.companyId) {
			return [];
		}
		return this.apiService.getCompanyPropertiesByCompanyId(
			this.companyId,
			filter
		);
	};

	get companyName(): string {
		return this.company?.name ?? '';
	}

	get showEntityTabs(): boolean {
		return this.hasGcmView || this.isEditingActiveCompany;
	}

	/**
	 * Flag-on Billing path without GCM: Users (ITE-1887) + Properties (ITE-11994).
	 * Does not open Relationships / Devices / Roles.
	 */
	get showBillingUsersOnly(): boolean {
		return (
			this.enableUpdatedSysAdmin &&
			this.hasBillingManagement &&
			!this.showEntityTabs
		);
	}

	/** Properties top-tab index for the flag-on shell (About is always 0). */
	get propertiesTabIndex(): number {
		if (this.showBillingUsersOnly) {
			// About, Users, Properties, Devices
			return 2;
		}
		// About, Relationships, Users, Properties
		return 3;
	}

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly apiService: ApiService,
		private readonly scService: SystemCapabilityService,
		private readonly destroyRef: DestroyRef
	) {
		this.companyId = this.route.snapshot.params.id;
	}

	async ngOnInit(): Promise<void> {
		this.hasGlobalCompanyManagement = this.scService.has(
			SystemCapabilityEnum.globalCompanyManagement
		);
		this.hasGcmView = this.scService.hasGcmView();
		this.hasBillingManagement = this.scService.hasBillingManagement();
		this.canAccessAuditLogs = this.scService.has(
			SystemCapabilityEnum.auditLogs
		);
		this.canSeeWhitelistTab =
			this.scService.isCompanyAdmin ||
			this.scService.isOracle ||
			this.hasGcmView ||
			this.hasBillingManagement;

		this.syncPropertyShellActive();
		this.router.events
			.pipe(
				filter(
					(event): event is NavigationEnd => event instanceof NavigationEnd
				),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => this.syncPropertyShellActive());

		const flagRequest = this.apiService
			.getFeatureFlagByName(FeatureFlagName.enableUpdatedSysAdmin)
			.catch(() => undefined);
		const companyRequest = this.loadCompanyResponse();
		this.initialCompanyResponse = companyRequest;
		const sentrilockRequest = this.apiService.getSentrilockCompanyId();
		const profileRequest = this.apiService.getUserProfile();

		try {
			const [flag, companyResponse, sentrilockId, userProfile] =
				await Promise.all([
					flagRequest,
					companyRequest,
					sentrilockRequest,
					profileRequest,
				]);
			this.enableUpdatedSysAdmin = !!flag?.value;
			this.company = companyResponse.company;
			this.updateBreadcrumbs();
			this.applyPropertyAccess(sentrilockId, userProfile);
			this.allowWhitelistMutate = this.computeAllowWhitelistMutate();
			this.allowDeviceWrites = this.computeAllowDeviceWrites();
			this.companyDetailsReady = true;
			this.ensurePropertiesTabSelectedForShell();
		} finally {
			// Keep the first-paint share only; recreated About tabs must refetch.
			this.initialCompanyResponse = undefined;
		}
	}

	updateBreadcrumbs(): void {
		this.companyAboutBreadcrumbLabel = `${this.company?.name}`;
		this.companyDevicesBreadcrumbLabel = `${this.company?.name}`;
		this.companyPropertiesBreadcrumbLabel = `${this.company?.name}`;
		this.companyRelationshipsBreadcrumbLabel = `${this.company?.name}`;
		this.companyUserBreadcrumbLabel = `${this.company?.name}`;
		this.companyRolesBreadcrumbLabel = `${this.company?.name}`;
		this.auditLogBreadcrumbLabel = `${this.company?.name}`;
	}

	/** About refetched after a save — keep header detail and tab breadcrumb labels in sync. */
	onCompanyUpdated(company: Company): void {
		this.company = company;
		this.updateBreadcrumbs();
	}

	loadCompanyResponse(): Promise<CompanyDetailResponse> {
		return this.apiService.getCompanyById(this.companyId);
	}

	requestCompany(): Promise<Company> {
		return this.loadCompanyResponse().then((response) => response.company);
	}

	async setCanViewProperties(): Promise<void> {
		const [sentrilockId, userProfile] = await Promise.all([
			this.apiService.getSentrilockCompanyId(),
			this.apiService.getUserProfile(),
		]);
		this.applyPropertyAccess(sentrilockId, userProfile);
	}

	applyPropertyAccess(sentrilockId: string, userProfile: UserProfile): void {
		const hasPropertyManagement = this.scService.has(
			SystemCapabilityEnum.propertyManagement
		);
		const hasGlobalCompanyMgmt = this.scService.has(
			SystemCapabilityEnum.globalCompanyManagement
		);
		const activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		let editingCompanyId = this.company?.id;
		if (!editingCompanyId) {
			editingCompanyId = activeCompanyId;
		}

		this.isEditingActiveCompany = activeCompanyId === editingCompanyId;
		this.companyIsSentrilock = editingCompanyId === sentrilockId;

		this.canViewProperties =
			(hasPropertyManagement && this.isEditingActiveCompany) ||
			(hasGlobalCompanyMgmt &&
				hasPropertyManagement &&
				!this.companyIsSentrilock);

		// Same Add rules as legacy CompanyProperties / /properties:
		// PM on active company (incl. SentriLock), or GCM act on a non-active non-SentriLock company.
		this.canAddProperty =
			(hasPropertyManagement && this.isEditingActiveCompany) ||
			(hasGlobalCompanyMgmt &&
				!this.isEditingActiveCompany &&
				!this.companyIsSentrilock);
	}

	/**
	 * Whitelist write chrome: Oracle, GCM act on non-SentriLock, or CA on active company.
	 * Support (GCM View) and Billing stay view-only (ITE-1887).
	 */
	computeAllowWhitelistMutate(): boolean {
		if (this.scService.isOracle) {
			return true;
		}
		if (this.scService.hasGcmAct() && !this.companyIsSentrilock) {
			return true;
		}
		return this.scService.isCompanyAdmin && this.isEditingActiveCompany;
	}

	/**
	 * Leaving Single Property: company top-tab clicks only update `tabIndex` and
	 * would keep the nested property route. Exit to `/company/:id/detail` so About /
	 * Users / Devices / etc. work again.
	 * Ignore Properties reselection here — shell entry snaps that index via
	 * `ensurePropertiesTabSelectedForShell`. List return is via Properties top-tab
	 * label click (`onPropertiesTopTabClick`); MatTab rarely re-emits for an
	 * already-selected tab.
	 */
	onCompanyTabIndexChange(index: number): void {
		if (!this.propertyShellActive || !this.enableUpdatedSysAdmin) {
			return;
		}
		if (index === this.propertiesTabIndex) {
			return;
		}
		void this.router.navigate(['/company', this.companyId, 'detail'], {
			queryParams: {
				tabIndex: index,
				propertyTab: null,
			},
			queryParamsHandling: 'merge',
		});
	}

	/**
	 * Close Single Property and show this company's Properties list. Bound on the
	 * Properties tab label so a re-click works while Properties stays selected.
	 */
	onPropertiesTopTabClick(): void {
		if (!this.propertyShellActive || !this.enableUpdatedSysAdmin) {
			return;
		}
		void this.router.navigate(['/company', this.companyId, 'detail'], {
			queryParams: {
				tabIndex: this.propertiesTabIndex,
				propertyTab: null,
			},
			queryParamsHandling: 'merge',
		});
	}

	private syncPropertyShellActive(): void {
		const childPath = this.route.snapshot.firstChild?.routeConfig?.path;
		this.propertyShellActive = childPath === 'property/:propertyId/detail';
		if (this.propertyShellActive && this.companyDetailsReady) {
			this.ensurePropertiesTabSelectedForShell();
		}
	}

	/** Keep Properties selected in the company shell while the Single Property route is open. */
	private ensurePropertiesTabSelectedForShell(): void {
		if (!this.propertyShellActive || !this.enableUpdatedSysAdmin) {
			return;
		}
		const current = this.route.snapshot.queryParamMap.get('tabIndex');
		const expected = String(this.propertiesTabIndex);
		if (current === expected) {
			return;
		}
		void this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { tabIndex: this.propertiesTabIndex },
			queryParamsHandling: 'merge',
			replaceUrl: true,
		});
	}

	/**
	 * Device list writes on company detail: GCM act on non-SentriLock, or DM on active company.
	 * Support (GCM View) and Billing stay view-only. Oracle with DM may write on the SentriLock company.
	 */
	computeAllowDeviceWrites(): boolean {
		if (this.companyIsSentrilock) {
			return (
				this.scService.isOracle &&
				this.scService.has(SystemCapabilityEnum.deviceManagement)
			);
		}
		if (this.scService.hasGcmAct()) {
			return true;
		}
		return (
			this.scService.has(SystemCapabilityEnum.deviceManagement) &&
			this.isEditingActiveCompany
		);
	}
}
